/**
 * AdminSources — Cash & Liquidity Hub (Phase 131, simplified June 2026).
 * Master Cash Registry for every bank account / card: the current Statement
 * Statement Balance Today, both typed in by Finance and stored as-is (no
 * derived figures). Internal Transfers are a standalone log that never
 * touches Revenue/Expense charts or any cash figure.
 * Strictly Founder & Admin (backend: require_roles("admin")).
 */
import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { Plus, Pencil, Trash2, CreditCard, Landmark, ArrowRightLeft, BarChart3, ExternalLink, UploadCloud, Loader2, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/dateFormat";
import { sortSources } from "@/lib/finance";

const EMPTY = { name: "", type: "bank_account", institution: "", last4: "", notes: "", is_active: true };
const EMPTY_TRANSFER = { from_source_id: "", to_source_id: "", amount: "", date: new Date().toISOString().slice(0, 10), notes: "" };
const fmt = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function AdminSources() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [totalCash, setTotalCash] = useState(0);
  const [transfers, setTransfers] = useState([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferDraft, setTransferDraft] = useState(EMPTY_TRANSFER);
  const [stmtDraft, setStmtDraft] = useState({}); // sourceId -> { statement_balance? } being typed
  const [savingId, setSavingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [summaries, setSummaries] = useState({}); // id -> data
  const [importingId, setImportingId] = useState(null); // sourceId currently uploading a statement CSV
  const csvInputRef = React.useRef(null);
  const csvTargetRef = React.useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/finance/sources/balances");
      setRows(data.sources || []);
      setTotalCash(data.total_studio_cash || 0);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadTransfers = async () => {
    try { const { data } = await api.get("/finance/internal-transfers"); setTransfers(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); loadTransfers(); }, []);

  const grouped = useMemo(() => ({
    bank_account: rows.filter((r) => r.type === "bank_account"),
    card: rows.filter((r) => r.type === "card"),
  }), [rows]);

  // Cards carry a balance we OWE the issuer, not cash-in-hand — surfaced
  // separately from Total Studio Cash so liquidity never looks better than
  // it really is. Both are plain sums of Statement Balance Today.
  const totalLiabilities = useMemo(
    () => rows.filter((r) => r.type === "card" && r.is_active).reduce((s, r) => s + Number(r.statement_balance || 0), 0),
    [rows]
  );
  const bankTotals = useMemo(() => {
    const banks = grouped.bank_account;
    return {
      statement: banks.reduce((s, r) => s + Number(r.statement_balance || 0), 0),
    };
  }, [grouped]);

  if (user && user.role !== "admin" && user.role !== "founder") {
    return (
      <div className="p-8 max-w-3xl mx-auto" data-testid="sources-403">
        <PageHeader eyebrow="Restricted" title="Cash & Liquidity" subtitle="Founders and admins only." />
        <div className="card-flat p-6 text-sm text-slate-600">You don&apos;t have access to this module.</div>
      </div>
    );
  }

  const startCreate = () => { setDraft(EMPTY); setEditingId(null); setOpen(true); };
  const startEdit = (r) => { setDraft({ ...r }); setEditingId(r.id); setOpen(true); };

  const save = async () => {
    if (!draft.name?.trim()) { toast.error("Name is required"); return; }
    try {
      if (editingId) {
        await api.patch(`/finance/sources/${editingId}`, draft);
        toast.success("Updated");
      } else {
        await api.post("/finance/sources", draft);
        toast.success("Added");
      }
      setOpen(false); load();
      // Feb 2026 — Account Filter Sync: let the Expenses/Revenue ledgers'
      // Account dropdowns refetch instantly, no page refresh needed.
      window.dispatchEvent(new CustomEvent("finance-sources-changed"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"? Expenses already tagged with this source will keep their value but the option will disappear from dropdowns.`)) return;
    try {
      await api.delete(`/finance/sources/${r.id}`);
      toast.success("Removed");
      load();
      window.dispatchEvent(new CustomEvent("finance-sources-changed"));
    }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const setField = (sid, key, val) => setStmtDraft((d) => ({ ...d, [sid]: { ...(d[sid] || {}), [key]: val } }));
  const draftFor = (r) => stmtDraft[r.id] || {};
  const isDirty = (r) => Object.keys(draftFor(r)).length > 0;

  const saveRow = async (r) => {
    const d = draftFor(r);
    if (!isDirty(r)) return;
    const payload = {};
    if (d.statement_balance !== undefined) payload.statement_balance = d.statement_balance === "" ? null : Number(d.statement_balance);
    if (Object.values(payload).some((v) => v !== null && Number.isNaN(v))) { toast.error("Enter a valid number"); return; }
    setSavingId(r.id);
    try {
      await api.patch(`/finance/sources/${r.id}`, payload);
      toast.success(`${r.name} saved`);
      setStmtDraft((prev) => { const n = { ...prev }; delete n[r.id]; return n; });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingId(null); }
  };

  // CSV Bank Statement Import — sets Statement Balance Today from the file's
  // latest closing balance. Bank Accounts only (Cards don't have statements).
  const triggerCsvImport = (sid) => { csvTargetRef.current = sid; csvInputRef.current?.click(); };
  const onCsvFileChosen = async (e) => {
    const file = e.target.files?.[0];
    const sid = csvTargetRef.current;
    e.target.value = "";
    if (!file || !sid) return;
    setImportingId(sid);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/finance/sources/${sid}/reconciliation/import-csv`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Statement balance set to ${fmt(data.statement_balance)} (closing balance on ${data.date_to})`);
      setStmtDraft((prev) => { const n = { ...prev }; delete n[sid]; return n; });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImportingId(null); }
  };

  const submitTransfer = async () => {
    if (!transferDraft.from_source_id || !transferDraft.to_source_id) { toast.error("Pick both accounts"); return; }
    if (transferDraft.from_source_id === transferDraft.to_source_id) { toast.error("Pick two different accounts"); return; }
    if (!transferDraft.amount || Number(transferDraft.amount) <= 0) { toast.error("Enter a transfer amount"); return; }
    try {
      await api.post("/finance/internal-transfers", { ...transferDraft, amount: Number(transferDraft.amount) });
      toast.success("Transfer logged");
      setTransferOpen(false); setTransferDraft(EMPTY_TRANSFER);
      load(); loadTransfers();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeTransfer = async (t) => {
    if (!window.confirm("Delete this transfer from the log?")) return;
    try { await api.delete(`/finance/internal-transfers/${t.id}`); toast.success("Removed"); load(); loadTransfers(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const loadSummary = async (id) => {
    if (summaries[id]) return;
    try { const { data } = await api.get(`/finance/sources/${id}/summary`); setSummaries((s) => ({ ...s, [id]: data })); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleExpand = (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    loadSummary(id);
  };

  const renderRow = (r) => {
    const isOpen = expandedId === r.id;
    const sum = summaries[r.id];
    const d = draftFor(r);
    const dirty = isDirty(r);
    const numInput = (key, fallback, testid, extra = "") => (
      <Input
        type="number"
        className={`rounded-sm h-8 text-xs w-32 text-right font-mono ${extra}`}
        placeholder="—"
        value={d[key] !== undefined ? d[key] : (fallback ?? "")}
        onChange={(e) => setField(r.id, key, e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") saveRow(r); }}
        data-testid={testid}
      />
    );
    return (
      <React.Fragment key={r.id}>
      <tr className={`hover:bg-slate-50 ${dirty ? "bg-orange-50/40" : ""}`} data-testid={`source-row-${r.id}`}>
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-sm flex items-center justify-center shrink-0 ${r.type === "card" ? "bg-purple-50 text-purple-700" : "bg-emerald-50 text-emerald-700"}`}>
              {r.type === "card" ? <CreditCard size={13} /> : <Landmark size={13} />}
            </div>
            <div className="min-w-0">
              <div className="font-medium text-sm text-slate-900 truncate" data-testid={`source-name-${r.id}`}>{r.name}</div>
              <div className="text-[10px] text-slate-500">{r.institution || "—"}{r.last4 && <span className="font-mono ml-1">·· {r.last4}</span>}{!r.is_active && <span className="ml-1 text-red-600">· inactive</span>}</div>
            </div>
          </div>
        </td>
        <td className="px-3 py-2.5 text-right">
          <div className="inline-flex items-center gap-1">
            {numInput("statement_balance", r.statement_balance, `source-statement-input-${r.id}`, "font-semibold text-slate-900")}
            {r.type === "bank_account" && (
              <button
                onClick={() => triggerCsvImport(r.id)}
                disabled={importingId === r.id}
                className="p-1.5 text-slate-400 hover:text-[#F54800] shrink-0"
                title="Import bank statement CSV — sets Statement Balance Today"
                data-testid={`source-import-csv-${r.id}`}
              >
                {importingId === r.id ? <Loader2 size={13} className="animate-spin" /> : <UploadCloud size={13} />}
              </button>
            )}
          </div>
          {!dirty && (() => {
            const ageDays = r.statement_balance_updated_at ? Math.floor((Date.now() - new Date(r.statement_balance_updated_at).getTime()) / 86400000) : null;
            const stale = r.type === "bank_account" && (ageDays === null || ageDays >= 7);
            if (stale) return (
              <div className="text-[10px] text-amber-700 font-medium mt-0.5 inline-flex items-center gap-1" data-testid={`source-statement-stale-${r.id}`}>
                <AlertTriangle size={10} /> {ageDays === null ? "Never entered" : `Not updated in ${ageDays}d`}
              </div>
            );
            return r.statement_balance_updated_at ? (
              <div className="text-[10px] text-slate-400 mt-0.5" data-testid={`source-statement-updated-${r.id}`}>Updated {formatDate(r.statement_balance_updated_at, { fallback: "—" })}</div>
            ) : null;
          })()}
        </td>
        <td className="px-3 py-2.5 text-right">
          <Button
            size="sm"
            variant={dirty ? "default" : "outline"}
            disabled={!dirty || savingId === r.id}
            onClick={() => saveRow(r)}
            className={`rounded-sm h-8 text-xs ${dirty ? "bg-[#F54800] hover:bg-[#D63E00] text-white" : ""}`}
            data-testid={`source-save-row-${r.id}`}
          >
            {savingId === r.id ? <Loader2 size={12} className="animate-spin mr-1" /> : <Save size={12} className="mr-1" />}Save
          </Button>
        </td>
        <td className="px-3 py-2.5 text-right">
          <div className="inline-flex gap-1">
            <button onClick={() => toggleExpand(r.id)} className={`p-1.5 ${isOpen ? "text-[#F54800]" : "text-slate-400 hover:text-[#F54800]"}`} title="View MTD summary" data-testid={`source-summary-${r.id}`}><BarChart3 size={12} /></button>
            <button onClick={() => startEdit(r)} className="p-1.5 text-slate-400 hover:text-slate-700" data-testid={`source-edit-${r.id}`}><Pencil size={12} /></button>
            <button onClick={() => remove(r)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`source-delete-${r.id}`}><Trash2 size={12} /></button>
          </div>
        </td>
      </tr>
      {isOpen && (
        <tr data-testid={`source-summary-panel-${r.id}`}>
          <td colSpan={4} className="bg-slate-50/60 border-t border-slate-100 p-4">
            {!sum && <div className="text-xs text-slate-400 italic">Loading…</div>}
            {sum && (
              <>
                <div className="grid grid-cols-2 gap-3 mb-3 max-w-md">
                  <div>
                    <div className="label-overline text-slate-500">MTD spend · {sum.month}</div>
                    <div className="font-display text-lg font-semibold text-slate-900" data-testid={`source-mtd-${r.id}`}>{fmt(sum.mtd_total)}</div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-wider">{sum.mtd_count} transaction{sum.mtd_count === 1 ? "" : "s"}</div>
                  </div>
                  <div>
                    <div className="label-overline text-slate-500">All-time spend</div>
                    <div className="font-display text-lg font-semibold text-slate-700">{fmt(sum.all_time_total)}</div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-wider">{sum.all_time_count} total</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3 max-w-md">
                  <div className="bg-white border border-slate-200 rounded-sm p-2">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Top vendor (MTD)</div>
                    <div className="text-xs font-medium text-slate-800 truncate mt-1">{sum.top_vendor?.name || "—"}</div>
                    <div className="text-[11px] text-[#F54800] font-mono">{sum.top_vendor ? fmt(sum.top_vendor.total) : "—"}</div>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-sm p-2">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Top category (MTD)</div>
                    <div className="text-xs font-medium text-slate-800 truncate mt-1">{sum.top_category?.department || "—"} · {sum.top_category?.category || "—"}</div>
                    <div className="text-[11px] text-[#F54800] font-mono">{sum.top_category ? fmt(sum.top_category.total) : "—"}</div>
                  </div>
                </div>
                <div className="label-overline text-slate-500 mb-1.5">Last 5 transactions</div>
                <div className="space-y-1">
                  {(sum.recent || []).length === 0 && <div className="text-[11px] text-slate-400 italic">No transactions yet on this source.</div>}
                  {(sum.recent || []).map((t) => (
                    <div key={t.id} className="flex items-center gap-2 text-xs bg-white border border-slate-200 rounded-sm px-2 py-1.5" data-testid={`source-txn-${t.id}`}>
                      <span className="text-slate-500 font-mono text-[10px] w-20 shrink-0">{formatDate(t.date, { fallback: "—" })}</span>
                      <span className="text-slate-700 truncate flex-1">{t.description || `${t.department || ""} · ${t.category || ""}`}{t.project_name && <span className="text-slate-400"> · {t.project_name}</span>}</span>
                      <span className="text-slate-500 truncate w-24">{t.vendor || "—"}</span>
                      <span className="font-mono text-[#F54800] w-20 text-right">{fmt(t.amount)}</span>
                      {t.drive_link && <a href={t.drive_link} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#F54800]"><ExternalLink size={10} /></a>}
                    </div>
                  ))}
                </div>
              </>
            )}
          </td>
        </tr>
      )}
      </React.Fragment>
    );
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto fade-in" data-testid="sources-page">
      <input ref={csvInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onCsvFileChosen} data-testid="source-csv-file-input" />
      <PageHeader
        eyebrow="Finance · Founders & Admins only"
        title="Cash & Liquidity"
        subtitle="Master Cash Registry — statement balances per account, plus the internal transfer log."
        actions={
          <div className="flex gap-2">
            <Dialog open={transferOpen} onOpenChange={(v) => { setTransferOpen(v); if (!v) setTransferDraft(EMPTY_TRANSFER); }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-sm" data-testid="new-transfer-btn"><ArrowRightLeft size={14} className="mr-1.5" />Internal transfer</Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm" data-testid="transfer-dialog">
                <DialogHeader>
                  <DialogTitle className="font-display">Internal transfer</DialogTitle>
                  <DialogDescription>Moves cash between two Sources. Never counted as Revenue or Expense on any chart.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                  <div>
                    <Label className="label-overline">From account *</Label>
                    <Select value={transferDraft.from_source_id} onValueChange={(v) => setTransferDraft({ ...transferDraft, from_source_id: v })}>
                      <SelectTrigger className="rounded-sm mt-1" data-testid="transfer-from"><SelectValue placeholder="Pick source" /></SelectTrigger>
                      <SelectContent>{sortSources(rows).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-overline">To account *</Label>
                    <Select value={transferDraft.to_source_id} onValueChange={(v) => setTransferDraft({ ...transferDraft, to_source_id: v })}>
                      <SelectTrigger className="rounded-sm mt-1" data-testid="transfer-to"><SelectValue placeholder="Pick source" /></SelectTrigger>
                      <SelectContent>{sortSources(rows).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="label-overline">Amount ₹ *</Label>
                      <Input type="number" className="rounded-sm mt-1" value={transferDraft.amount} onChange={(e) => setTransferDraft({ ...transferDraft, amount: e.target.value })} data-testid="transfer-amount" />
                    </div>
                    <div>
                      <Label className="label-overline">Date</Label>
                      <Input type="date" className="rounded-sm mt-1" value={transferDraft.date} onChange={(e) => setTransferDraft({ ...transferDraft, date: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <Label className="label-overline">Notes</Label>
                    <Input className="rounded-sm mt-1" placeholder="e.g. Moving idle cash to FD account" value={transferDraft.notes} onChange={(e) => setTransferDraft({ ...transferDraft, notes: e.target.value })} />
                  </div>
                </div>
                <DialogFooter><Button onClick={submitTransfer} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="transfer-submit">Log transfer</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setDraft(EMPTY); setEditingId(null); } }}>
              <DialogTrigger asChild>
                <Button onClick={startCreate} data-testid="add-source-btn" className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white">
                  <Plus size={14} className="mr-1.5" />Add source
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm" data-testid="source-dialog">
                <DialogHeader><DialogTitle className="font-display">{editingId ? "Edit" : "Add"} source</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <div>
                    <Label className="label-overline">Name *</Label>
                    <Input className="rounded-sm mt-1" placeholder="e.g. HDFC Current · 1234" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="source-name-input" />
                  </div>
                  <div>
                    <Label className="label-overline">Type *</Label>
                    <Select value={draft.type} onValueChange={(v) => setDraft({ ...draft, type: v })}>
                      <SelectTrigger className="rounded-sm mt-1" data-testid="source-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bank_account">Bank account</SelectItem>
                        <SelectItem value="card">Card</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="label-overline">Institution</Label>
                      <Input className="rounded-sm mt-1" placeholder="HDFC / Amex / …" value={draft.institution || ""} onChange={(e) => setDraft({ ...draft, institution: e.target.value })} />
                    </div>
                    <div>
                      <Label className="label-overline">Last 4 digits</Label>
                      <Input className="rounded-sm mt-1 font-mono" maxLength={4} value={draft.last4 || ""} onChange={(e) => setDraft({ ...draft, last4: e.target.value.replace(/[^0-9]/g, "").slice(0, 4) })} />
                    </div>
                  </div>
                  <div>
                    <Label className="label-overline">Notes</Label>
                    <Input className="rounded-sm mt-1" value={draft.notes || ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="is_active" checked={!!draft.is_active} onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} />
                    <Label htmlFor="is_active" className="text-xs">Active (show in dropdowns)</Label>
                  </div>
                </div>
                <DialogFooter><Button onClick={save} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="source-save">{editingId ? "Save" : "Add"}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="mb-6 flex flex-wrap gap-4">
        <div className="max-w-xs">
          <StatCard label="Total Studio Cash" value={fmt(totalCash)} hint={`Statement balances across ${rows.filter((r) => r.is_active && r.type === "bank_account").length} active bank account(s)`} data-testid="total-studio-cash-value" />
        </div>
        <div className="max-w-xs">
          <StatCard label="Outstanding Liabilities" value={fmt(totalLiabilities)} hint={`Statement balances across ${rows.filter((r) => r.is_active && r.type === "card").length} active card(s)`} data-testid="total-outstanding-liabilities-value" />
        </div>
      </div>

      <Tabs defaultValue="registry" className="w-full">
        <TabsList className="rounded-sm">
          <TabsTrigger value="registry" data-testid="tab-cash-registry" className="rounded-sm">Master Cash Registry</TabsTrigger>
          <TabsTrigger value="transfers" data-testid="tab-internal-transfers" className="rounded-sm">Internal Transfers · {transfers.length}</TabsTrigger>
        </TabsList>

        <TabsContent value="registry" className="mt-4">
          {["bank_account", "card"].map((type) => (
            <div key={type} className="mb-6">
              <div className="label-overline text-slate-500 mb-2">{type === "bank_account" ? "Bank accounts" : "Cards"} · {grouped[type].length}</div>
              {grouped[type].length === 0 ? (
                <div className="text-xs text-slate-400 italic">No {type === "bank_account" ? "bank accounts" : "cards"} yet.</div>
              ) : (
                <div className="card-flat overflow-x-auto">
                  <table className="w-full text-sm min-w-[720px]">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                      <tr>
                        {["Account", "Statement Balance Today", "Save", "Actions"].map((h) => (
                          <th key={h} className={`px-3 py-2 font-medium ${h === "Account" ? "text-left" : "text-right"}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">{grouped[type].map(renderRow)}</tbody>
                    {type === "bank_account" && (
                      <tfoot data-testid="bank-accounts-totals-row">
                        <tr className="bg-slate-50 border-t-2 border-slate-200 font-semibold">
                          <td className="px-3 py-2.5 text-xs text-slate-700">Total ({grouped.bank_account.length})</td>
                          <td className="px-3 py-2.5 text-right font-mono text-sm text-slate-900" data-testid="bank-totals-statement">{fmt(bankTotals.statement)}</td>
                          <td colSpan={2}></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="transfers" className="mt-4">
          <div className="card-flat overflow-x-auto" data-testid="transfers-log">
            <table className="w-full text-sm min-w-[800px]">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>{["Date", "From", "To", "Amount ₹", "Notes", "By", "Actions"].map((h) => <th key={h} className="px-3 py-2 font-medium text-left">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {transfers.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50" data-testid={`transfer-row-${t.id}`}>
                    <td className="px-3 py-2 text-xs">{formatDate(t.date, { fallback: "—" })}</td>
                    <td className="px-3 py-2 text-xs">{t.from_source_name}</td>
                    <td className="px-3 py-2 text-xs">{t.to_source_name}</td>
                    <td className="px-3 py-2 text-xs font-mono">{fmt(t.amount)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{t.notes || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{t.created_by_name || "—"}</td>
                    <td className="px-3 py-2 text-right"><button onClick={() => removeTransfer(t)} className="p-1 text-slate-400 hover:text-red-600" data-testid={`transfer-delete-${t.id}`}><Trash2 size={12} /></button></td>
                  </tr>
                ))}
                {transfers.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-xs text-slate-400">No internal transfers logged yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
